import React, { useState, useCallback, useEffect } from 'react';
import DriverDataUpload from './DriverDataUpload';
import DriverAnalysisForm from './DriverAnalysisForm';
import api from '../services/api';

const STEPS = [
    { id: 1, label: 'Project Selection', desc: 'Choose a project to analyse' },
    { id: 2, label: 'Variable Selection', desc: 'Upload or choose an SPSS file' },
    { id: 3, label: 'Driver Analysis',   desc: 'Configure and run driver analysis' },
];

// ─── Vertical step sidebar ────────────────────────────────────────────────────
const StepSidebar = ({ currentStep, stepReady, onStepClick }) => (
    <div style={{
        width: 220, flexShrink: 0,
        background: '#fff',
        borderRight: '1px solid #e5e7eb',
        padding: '24px 0',
        display: 'flex', flexDirection: 'column', gap: 4,
    }}>
        {STEPS.map((step) => {
            const isActive    = currentStep === step.id;
            const isCompleted = currentStep > step.id;
            const clickable   = stepReady(step.id);

            return (
                <button
                    key={step.id}
                    type="button"
                    onClick={() => clickable && onStepClick(step.id)}
                    disabled={!clickable}
                    style={{
                        display: 'flex', alignItems: 'flex-start', gap: 12,
                        padding: '12px 20px',
                        background: isActive ? '#eef2ff' : 'transparent',
                        borderLeft: isActive ? '3px solid #4f46e5' : '3px solid transparent',
                        border: 'none', borderLeft: isActive ? '3px solid #4f46e5' : '3px solid transparent',
                        cursor: clickable ? 'pointer' : 'default',
                        opacity: clickable ? 1 : 0.45,
                        textAlign: 'left', width: '100%',
                        transition: 'background 0.15s',
                    }}
                >
                    <span style={{
                        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700,
                        background: isActive ? '#4f46e5' : isCompleted ? '#22c55e' : '#e5e7eb',
                        color: (isActive || isCompleted) ? '#fff' : '#6b7280',
                    }}>
                        {isCompleted ? '✓' : step.id}
                    </span>
                    <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: isActive ? '#4338ca' : '#374151' }}>
                            {step.label}
                        </div>
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{step.desc}</div>
                    </div>
                </button>
            );
        })}
    </div>
);

// ─── Small inline spinner ─────────────────────────────────────────────────────
const Spinner = () => (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'60px 0', color:'#4f46e5' }}>
        <svg width="28" height="28" style={{ marginRight:10, flexShrink:0,
            animation:'da-spin 0.9s linear infinite', transformOrigin:'center' }}
             fill="none" viewBox="0 0 24 24">
            <style>{`@keyframes da-spin{to{transform:rotate(360deg)}}`}</style>
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" style={{ opacity:0.2 }} />
            <path fill="currentColor" style={{ opacity:0.8 }}
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <span style={{ fontSize:13, fontWeight:500 }}>Loading projects…</span>
    </div>
);

// ─── Project selector ─────────────────────────────────────────────────────────
const ProjectSelector = ({ selectedProject, onSelect }) => {
    const [segments, setSegments] = useState([]);
    const [loading, setLoading]   = useState(true);
    const [error, setError]       = useState('');
    const [search, setSearch]     = useState('');
    const [showAll, setShowAll]   = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const email = localStorage.getItem('username');
                const namesRes = await api.get('users/cascading_names/', {
                    params: { email },
                    headers: { Authorization: `Token ${localStorage.getItem('Token')}` },
                });
                const names = namesRes.data;
                if (!names || names.length === 0) { setSegments([]); return; }
                const param = names.join('&name=');
                const segRes = await api.get(
                    `api/segments/fetch-segments-by-name?name=${param}`,
                    { headers: { Authorization: `Token ${localStorage.getItem('Token')}` } }
                );
                setSegments(segRes.data || []);
            } catch {
                setError('Failed to load projects. Please refresh and try again.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const filtered = segments.filter(s => {
        const q = search.toLowerCase();
        const matchSearch =
            (s.segment_name || '').toLowerCase().includes(q) ||
            (s.project_name || '').toLowerCase().includes(q) ||
            (s.job_number   || '').toLowerCase().includes(q);
        const matchStatus = showAll || (s.status !== 'Closed' && s.status !== 'Hold');
        return matchSearch && matchStatus;
    });

    if (loading) return <Spinner />;
    if (error)   return <div style={{ textAlign:'center', padding:'40px 0', color:'#ef4444', fontSize:13 }}>{error}</div>;

    return (
        <div>
            {/* Search + toggle */}
            <div style={{ display:'flex', gap:10, marginBottom:16 }}>
                <div style={{ position:'relative', flex:1 }}>
                    <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'#9ca3af', fontSize:14 }}>🔍</span>
                    <input
                        type="text"
                        placeholder="Search project or job number…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{
                            width:'100%', paddingLeft:32, paddingRight:12, paddingTop:8, paddingBottom:8,
                            fontSize:13, border:'1px solid #d1d5db', borderRadius:8, outline:'none',
                            boxSizing:'border-box',
                        }}
                    />
                </div>
                <button
                    type="button"
                    onClick={() => setShowAll(v => !v)}
                    style={{
                        padding:'8px 14px', fontSize:12, fontWeight:500, borderRadius:8, cursor:'pointer',
                        border: showAll ? 'none' : '1px solid #d1d5db',
                        background: showAll ? '#4f46e5' : '#fff',
                        color: showAll ? '#fff' : '#374151',
                    }}
                >
                    {showAll ? 'Active only' : 'Show all'}
                </button>
            </div>

            {/* Project grid */}
            {filtered.length === 0 ? (
                <div style={{ textAlign:'center', padding:'40px 0', color:'#9ca3af', fontSize:13 }}>No projects found.</div>
            ) : (
                <div style={{
                    display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))',
                    gap:12, maxHeight:420, overflowY:'auto', paddingRight:4,
                }}>
                    {filtered.map((seg, i) => {
                        const key = `${seg.job_number}-${seg.segment_name}-${i}`;
                        const isSel =
                            selectedProject &&
                            selectedProject.job_number   === seg.job_number &&
                            selectedProject.segment_name === seg.segment_name;
                        const statusColor =
                            seg.status === 'Active' ? { bg:'#dcfce7', txt:'#16a34a' } :
                            seg.status === 'Hold'   ? { bg:'#fef9c3', txt:'#a16207' } :
                                                      { bg:'#f3f4f6', txt:'#6b7280' };
                        return (
                            <button
                                key={key}
                                type="button"
                                onClick={() => onSelect(seg)}
                                style={{
                                    textAlign:'left', padding:'12px 14px', borderRadius:12, cursor:'pointer',
                                    border: isSel ? '2px solid #4f46e5' : '2px solid #e5e7eb',
                                    background: isSel ? '#eef2ff' : '#fff',
                                    boxShadow: isSel ? '0 0 0 3px rgba(79,70,229,0.1)' : 'none',
                                    transition:'border-color 0.15s, background 0.15s',
                                }}
                            >
                                <div style={{ fontSize:13, fontWeight:600, color: isSel ? '#4338ca' : '#1f2937',
                                              whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                                    {seg.segment_name || '—'}
                                </div>
                                <div style={{ fontSize:11, color:'#6b7280', marginTop:2,
                                              whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                                    {seg.project_name || ''}
                                </div>
                                <div style={{ display:'flex', gap:6, marginTop:8, flexWrap:'wrap' }}>
                                    {seg.job_number && (
                                        <span style={{ fontSize:10, background:'#f3f4f6', color:'#6b7280',
                                                       borderRadius:4, padding:'2px 6px' }}>
                                            {seg.job_number}
                                        </span>
                                    )}
                                    {seg.status && (
                                        <span style={{ fontSize:10, borderRadius:4, padding:'2px 6px',
                                                       background: statusColor.bg, color: statusColor.txt }}>
                                            {seg.status}
                                        </span>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ─── Shared UI helpers ────────────────────────────────────────────────────────
const NextBtn = ({ onClick, label }) => (
    <button type="button" onClick={onClick} style={{
        display:'inline-flex', alignItems:'center', gap:8,
        padding:'10px 24px', background:'#4f46e5', color:'#fff',
        fontSize:13, fontWeight:500, borderRadius:8, border:'none',
        cursor:'pointer', boxShadow:'0 1px 3px rgba(0,0,0,0.15)',
    }}>
        {label} →
    </button>
);

const BackBtn = ({ onClick, label }) => (
    <button type="button" onClick={onClick} style={{
        display:'inline-flex', alignItems:'center', gap:6,
        padding:'8px 14px', background:'#fff', color:'#374151',
        fontSize:13, fontWeight:500, borderRadius:8,
        border:'1px solid #d1d5db', cursor:'pointer',
    }}>
        ← {label}
    </button>
);

const SuccessPill = ({ text }) => (
    <div style={{
        display:'inline-flex', alignItems:'center', gap:8,
        background:'#f0fdf4', border:'1px solid #bbf7d0',
        borderRadius:8, padding:'8px 16px', fontSize:13, color:'#166534',
    }}>
        ✓ {text}
    </div>
);

const ContextPill = ({ icon, text }) => (
    <div style={{
        display:'inline-flex', alignItems:'center', gap:6,
        background:'#f9fafb', border:'1px solid #e5e7eb',
        borderRadius:8, padding:'6px 12px', fontSize:12, color:'#374151',
        maxWidth:260, overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis',
    }}>
        {icon} {text}
    </div>
);

// ─── Main page ────────────────────────────────────────────────────────────────
const DriverAnalysisPage = () => {
    const [currentStep, setCurrentStep]           = useState(1);
    const [selectedProject, setSelectedProject]   = useState(null);
    const [columns, setColumns]                   = useState([]);
    const [binaryColumns, setBinaryColumns]       = useState([]);
    const [valueLabels, setValueLabels]           = useState({});
    const [fileId, setFileId]                     = useState(null);
    const [selectedFileName, setSelectedFileName] = useState('');

    const projectReady = !!selectedProject;
    const fileReady    = Array.isArray(columns) && columns.length > 0 && !!fileId;

    const stepReady = useCallback((id) => {
        if (id === 1) return true;
        if (id === 2) return projectReady;
        if (id === 3) return projectReady && fileReady;
        return false;
    }, [projectReady, fileReady]);

    const handleSetFileId = useCallback((id) => {
        setFileId(id);
        setSelectedFileName(id || '');
    }, []);

    const contentStyle = {
        flex: 1, padding: currentStep === 3 ? '24px 32px' : '24px 32px',
        overflowY: 'auto', minWidth: 0,
    };

    return (
        <div style={{ display:'flex', height:'calc(100vh - 80px)', overflow:'hidden', background:'#f9fafb' }}>

            {/* Left step sidebar */}
            <StepSidebar
                currentStep={currentStep}
                stepReady={stepReady}
                onStepClick={setCurrentStep}
            />

            {/* Right content */}
            <div style={contentStyle}>

                {/* ── Step 1: Project Selection ── */}
                {currentStep === 1 && (
                    <>
                        <h2 style={{ fontSize:16, fontWeight:600, color:'#111827', marginBottom:20 }}>
                            Select a Project
                        </h2>
                        <div style={{ background:'#fff', borderRadius:16, border:'1px solid #e5e7eb',
                                      padding:'20px', boxShadow:'0 1px 3px rgba(0,0,0,0.05)' }}>
                            <ProjectSelector
                                selectedProject={selectedProject}
                                onSelect={setSelectedProject}
                            />
                        </div>
                        {projectReady && (
                            <div style={{ marginTop:20, display:'flex', alignItems:'center',
                                          justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
                                <SuccessPill text={`${selectedProject.segment_name}${selectedProject.project_name ? ' — ' + selectedProject.project_name : ''}`} />
                                <NextBtn onClick={() => setCurrentStep(2)} label="Select Variables" />
                            </div>
                        )}
                    </>
                )}

                {/* ── Step 2: Variable Selection ── */}
                {currentStep === 2 && (
                    <>
                        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, flexWrap:'wrap' }}>
                            <BackBtn onClick={() => setCurrentStep(1)} label="Back to Projects" />
                            <ContextPill icon="📁" text={selectedProject?.segment_name || ''} />
                        </div>
                        <DriverDataUpload
                            setColumns={setColumns}
                            setFileId={handleSetFileId}
                            setBinaryColumns={setBinaryColumns}
                            setValueLabels={setValueLabels}
                            selectedProject={selectedProject?.segment_name || ''}
                        />
                        {fileReady && (
                            <div style={{ marginTop:20, display:'flex', alignItems:'center',
                                          justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
                                <SuccessPill text={`${selectedFileName} — ${columns.length} variables ready`} />
                                <NextBtn onClick={() => setCurrentStep(3)} label="Run Driver Analysis" />
                            </div>
                        )}
                    </>
                )}

                {/* ── Step 3: Driver Analysis ── */}
                {currentStep === 3 && (
                    <>
                        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, flexWrap:'wrap' }}>
                            <BackBtn onClick={() => setCurrentStep(2)} label="Back to Variables" />
                            <ContextPill icon="📁" text={selectedProject?.segment_name || ''} />
                            <ContextPill icon="📄" text={selectedFileName} />
                        </div>
                        <DriverAnalysisForm
                            columns={columns}
                            binaryColumns={binaryColumns}
                            valueLabels={valueLabels}
                            fileId={fileId}
                        />
                    </>
                )}

            </div>
        </div>
    );
};

export default DriverAnalysisPage;
